import React, { forwardRef, useImperativeHandle } from "react"
import useReportTableContent from "features/Reporting/hooks/ReportTable/useReportTableContent"
import StatisticsTable from "shared/ui/Table/StatisticTable.component"
import ReportTableSettings from "./ReportTableSettings.component"

// Types
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { TabSettings } from "types/ReportingDashboard/reporting.types"

/**
 * Props interface for ReportTableContent component
 */
export interface ReportTableContentProps {
  /** Data codebook for variable information */
  codebook: CodebookData
  /** Query instance for data operations */
  queryInstance: QueryInstance | null
  /** Current filter configuration */
  filter: string
  /** Unique tab key identifier */
  tabKey: string
  /** Auto-run script content */
  autoRunScriptContent: string
  /** Initial settings from configuration */
  initialSettings: TabSettings
  /** Callback when settings change */
  onSettingsChange: (settings: TabSettings) => void
  /** Function to request sequential execution */
  onRequestExecution: (tabKey: string, reason: string) => void
}

/**
 * Reference interface for ReportTableContent component
 */
export interface ReportTableContentRef {
  /** Get table data for export */
  getTableData: (skipRun?: boolean) => Promise<(string | number | null)[][]>
  /** Trigger script execution */
  triggerRunScript: () => Promise<boolean>
  /** Check if table has data */
  hasData: () => boolean
  /** Check if script is currently running */
  readonly isRunningScript: boolean
}

/**
 * ReportTableContent component provides a configurable table for reporting data.
 * It handles script generation, data loading, and table display with the ability
 * to configure rows, columns, and statistical operators.
 */
const ReportTableContent = forwardRef<
  ReportTableContentRef,
  ReportTableContentProps
>(
  (
    {
      codebook,
      queryInstance,
      filter,
      tabKey,
      autoRunScriptContent,
      initialSettings,
      onSettingsChange,
      onRequestExecution,
    },
    ref
  ) => {
    // ========================================================================================
    // CUSTOM HOOK - ALL BUSINESS LOGIC
    // ========================================================================================

    const {
      // State and computed values
      transformedTableData,
      rowVariables,
      columnVariables,
      selectedOperators,
      tableOperationType,
      removedElements,
      isRunningScript,
      selectOptions,
      operatorOptions,

      // Actions
      setRowVariables,
      setColumnVariables,
      setSelectedOperators,
      setPersistentRows,
      handleTableOperationTypeChange,
      handleRun,
      runScript,
      triggerRunScript,
      getTableData,
      hasData,
    } = useReportTableContent({
      codebook,
      queryInstance,
      filter,
      tabKey,
      autoRunScriptContent,
      initialSettings,
      onSettingsChange,
      onRequestExecution,
    })

    // ========================================================================================
    // REF METHODS
    // ========================================================================================

    useImperativeHandle(
      ref,
      () => ({
        getTableData,
        triggerRunScript,
        hasData,
        get isRunningScript() {
          return isRunningScript
        },
      }),
      [getTableData, triggerRunScript, hasData, isRunningScript]
    )

    // ========================================================================================
    // RENDER
    // ========================================================================================

    return (
      <div className="flex flex-col h-full w-full">
        {/* Settings Panel */}
        <ReportTableSettings
          isVisible={false} // Collapsible by default
          selectOptions={selectOptions}
          rowVariables={rowVariables}
          setRowVariables={setRowVariables}
          columnVariables={columnVariables}
          setColumnVariables={setColumnVariables}
          selectedOperators={selectedOperators}
          setSelectedOperators={setSelectedOperators}
          operatorOptions={operatorOptions}
          tableOperationType={tableOperationType}
          onTableOperationTypeChange={handleTableOperationTypeChange}
          onRun={handleRun}
          isLoading={isRunningScript}
        />

        {/* Table Display */}
        <div className="flex-1 overflow-auto">
          {isRunningScript && (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">Loading table data...</div>
            </div>
          )}

          {!isRunningScript && transformedTableData && (
            <StatisticsTable
              transformedData={transformedTableData}
              onUpdateData={runScript}
              removedElements={removedElements}
              setPersistentRows={setPersistentRows}
            />
          )}

          {!isRunningScript && !transformedTableData && (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">
                Select row and column variables, then click "Run Cross
                Tabulation" to generate a table.
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
)

ReportTableContent.displayName = "ReportTableContent"
export default ReportTableContent
